/**
 * Turning a photograph off somebody's phone into something worth uploading.
 *
 * ⚠️ NO IMAGE LIBRARY. `createImageBitmap` decodes, `OffscreenCanvas` resizes, and
 * `convertToBlob({ type: 'image/webp' })` encodes — all of it built into every browser this product
 * targets, all of it available inside a worker. A compression library here would be hundreds of
 * kilobytes shipped to every visitor to do what the browser already does natively, which is the same
 * trade already taken for the R2 signer and the absent AI SDK.
 *
 * ⚠️ IT RUNS IN A WORKER BECAUSE DECODING A 12-MEGAPIXEL JPEG BLOCKS. On a mid-range Android that is
 * most of a second with the main thread frozen — no scrolling, no typing, no spinner animating. The
 * work is the same either way; where it happens is the difference between "that took a moment" and
 * "the page hung".
 *
 * ⚠️ AND RE-ENCODING STRIPS EXIF FOR FREE, WHICH IS THE PRIVACY POINT. A shop owner photographs their
 * premises on a phone and the JPEG carries GPS coordinates; if they took it at home, publishing it
 * publishes their home address. A canvas knows nothing about metadata, so none survives — and it
 * never leaves the machine in the first place, which is strictly better than stripping it server-side
 * after they have already sent it.
 *
 * The server re-checks everything anyway: the client is not a trust boundary (see `routes/media.ts`).
 */

import type { Orientation } from '@ksa/model'

/**
 * ⚠️ SIZED BY WHAT THE BLOCK WILL DO WITH IT, NOT BY A SINGLE MAXIMUM. The registry already declares
 * an orientation per field, so a hero knows it is wide and a team photograph knows it is tall. One
 * global "max 1600px" would send a 1600×1600 square for a slot rendered 300px wide in a grid of six.
 *
 * These are roughly twice the largest CSS size each slot is drawn at, which covers a 2× screen.
 */
const TARGET: Record<Orientation, { w: number; h: number }> = {
  landscape: { w: 1600, h: 900 },
  portrait: { w: 900, h: 1200 },
  square: { w: 1200, h: 1200 },
}

/** Good enough that nobody looks closely, small enough for a Saudi mobile connection. */
const QUALITY = 0.82

const WORKER_SOURCE = `
self.onmessage = async (event) => {
  const { file, w, h, quality } = event.data
  try {
    const bitmap = await createImageBitmap(file)

    // Cover, not stretch: fill the frame and crop the overflow, so a portrait photograph dropped into
    // a wide slot is cropped rather than squashed. Never scale UP — an image smaller than the target
    // is left alone, because enlarging it adds bytes and no detail.
    const scale = Math.min(Math.max(w / bitmap.width, h / bitmap.height), 1)
    const width = Math.round(bitmap.width * scale)
    const height = Math.round(bitmap.height * scale)

    const canvas = new OffscreenCanvas(width, height)
    const context = canvas.getContext('2d')
    context.drawImage(bitmap, 0, 0, width, height)
    bitmap.close()

    const blob = await canvas.convertToBlob({ type: 'image/webp', quality })
    const buffer = await blob.arrayBuffer()
    self.postMessage({ ok: true, buffer, type: blob.type }, [buffer])
  } catch (error) {
    self.postMessage({ ok: false, message: String(error && error.message ? error.message : error) })
  }
}
`

/**
 * ⚠️ THE WORKER IS BUILT FROM A BLOB URL RATHER THAN A SEPARATE ENTRY POINT. It is thirty lines that
 * exist only for this function; a second Vite entry, its own build output and its own hashed filename
 * is more moving parts than the code inside it. The URL is revoked as soon as the worker starts.
 */
function spawn(): Worker {
  const url = URL.createObjectURL(new Blob([WORKER_SOURCE], { type: 'text/javascript' }))
  const worker = new Worker(url)
  URL.revokeObjectURL(url)
  return worker
}

export class UploadRefused extends Error {}

/** Resize and re-encode, off the main thread. Falls back to the original bytes if anything refuses. */
export async function prepareImage(file: File, orientation: Orientation): Promise<Blob> {
  const target = TARGET[orientation] ?? TARGET.landscape

  // ⚠️ A BROWSER WITHOUT `OffscreenCanvas` STILL UPLOADS. It sends the original, which the server
  // accepts if it is a JPEG, PNG or WebP within the size cap — worse for bandwidth and it keeps its
  // metadata, and still better than a customer who cannot add a picture at all.
  if (typeof Worker === 'undefined' || typeof OffscreenCanvas === 'undefined') return file

  return new Promise<Blob>((resolve) => {
    const worker = spawn()
    // If the worker wedges, the customer must not be left watching a spinner for ever.
    const timer = window.setTimeout(() => {
      worker.terminate()
      resolve(file)
    }, 20_000)

    worker.onmessage = (event: MessageEvent) => {
      window.clearTimeout(timer)
      worker.terminate()
      const data = event.data as { ok: boolean; buffer?: ArrayBuffer; type?: string }
      if (!data.ok || !data.buffer) return resolve(file)
      resolve(new Blob([data.buffer], { type: data.type ?? 'image/webp' }))
    }
    worker.onerror = () => {
      window.clearTimeout(timer)
      worker.terminate()
      resolve(file)
    }

    worker.postMessage({ file, w: target.w, h: target.h, quality: QUALITY })
  })
}
